import { Component, inject, signal } from '@angular/core';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';

@Component({
  selector: 'app-partners-resellers-page',
  template: `
    <!-- Hero -->
    <app-page-section backgroundColor="secondary" [waves]="true" [noContainer]="true">
      <div class="container max-w-4xl text-center">
        <div class="flex items-center justify-center gap-3 text-white/40">
          <span class="text-2xl font-light">/</span>
          <i class="fal fa-handshake text-white text-2xl"></i>
          <span class="text-2xl font-light">/</span>
        </div>
        <div class="mt-6 text-white">
          <h1 class="mt-6" [innerHTML]="'partners.hero.title' | transloco"></h1>
          <p class="mt-6 text-base text-balance text-white/70">{{ 'partners.hero.description' | transloco }}</p>
        </div>
        <div class="mt-12 flex flex-col gap-4 max-w-xl mx-auto">
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event); openFormula.set('apporteur')" class="flex items-end justify-between rounded-2xl bg-secondary-900 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] p-6 no-underline hover:bg-gray-900 hover:border-white/15 transition-colors group">
            <div class="text-left self-center">
              <p class="text-lg font-semibold text-white/80 group-hover:text-white transition-colors">{{ 'partners.label.apporteur' | transloco }}</p>
              <p class="mt-1 text-sm text-white/40">{{ 'partners.hero.card-apporteur-amount' | transloco }}</p>
            </div>
            <span class="flex items-end gap-2 font-sans text-5xl sm:text-7xl font-medium text-white/70 leading-none shrink-0 ml-6">20<i class="fas fa-percent text-lg sm:text-2xl mb-1"></i></span>
          </a>
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event); openFormula.set('revendeur')" class="flex items-end justify-between rounded-2xl bg-secondary-900 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] p-6 no-underline hover:bg-gray-900 hover:border-white/15 transition-colors group">
            <div class="text-left self-center">
              <p class="text-lg font-semibold text-white/80 group-hover:text-white transition-colors">{{ 'partners.label.revendeur' | transloco }}</p>
              <p class="mt-1 text-sm text-white/40" [innerHTML]="'partners.hero.card-revendeur-amount' | transloco"></p>
            </div>
            <span class="flex items-end gap-2 font-sans text-5xl sm:text-7xl font-medium text-white/70 leading-none shrink-0 ml-6">50<i class="fas fa-percent text-lg sm:text-2xl mb-1"></i></span>
          </a>
        </div>
        <div class="mt-10 flex justify-center gap-3">
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event)"><app-button variant="primary" icon="arrow-right">{{ 'partners.hero.cta-discover' | transloco }}</app-button></a>
          <a href="#pour-qui" (click)="scrollTo('pour-qui', $event)"><app-button variant="dark" [pill]="true">{{ 'partners.hero.cta-for-who' | transloco }}</app-button></a>
        </div>
      </div>
    </app-page-section>

    <div class="pt-16 xl:pt-0"></div>

    <!-- Pour qui ? -->
    <app-page-section id="pour-qui">
      <div class="relative rounded-3xl px-4 py-14 sm:px-8 lg:px-14 lg:py-16 overflow-hidden" style="background: linear-gradient(to bottom, #f5f7f9 0%, #fafbfc 50%, #ffffff 100%)">
        <div class="text-center">
          <app-public-tag>{{ 'partners.for-who.tag' | transloco }}</app-public-tag>
          <h2 class="mt-6">{{ 'partners.for-who.title' | transloco }}</h2>
          <p class="mt-4 max-w-2xl mx-auto text-base">
            {{ 'partners.for-who.description' | transloco }}
          </p>
        </div>
        <div class="mt-12 grid grid-cols-1 gap-8 sm:gap-x-14 sm:grid-cols-2 sm:max-w-[55%] sm:mx-auto auto-rows-fr">
          <app-public-info-card icon="building">
            <span title>{{ 'partners.for-who.card-client.title' | transloco }}</span>
            <span description>{{ 'partners.for-who.card-client.description' | transloco }}</span>
          </app-public-info-card>
          <app-public-info-card icon="coins">
            <span title>{{ 'partners.for-who.card-income.title' | transloco }}</span>
            <span description>{{ 'partners.for-who.card-income.description' | transloco }}</span>
          </app-public-info-card>
          <app-public-info-card icon="laptop-code">
            <span title>{{ 'partners.for-who.card-reseller.title' | transloco }}</span>
            <span description>{{ 'partners.for-who.card-reseller.description' | transloco }}</span>
          </app-public-info-card>
          <app-public-info-card icon="map-marker-alt" [badge]="'partners.for-who.card-suisse.badge' | transloco">
            <span title>{{ 'partners.for-who.card-suisse.title' | transloco }}</span>
            <span description>{{ 'partners.for-who.card-suisse.description' | transloco }}</span>
          </app-public-info-card>
        </div>
        <div class="mt-10 flex justify-center">
          <a href="#contact" (click)="scrollTo('contact', $event)">
            <app-button variant="primary" icon="envelope">{{ 'partners.cta-contact' | transloco }}</app-button>
          </a>
        </div>
      </div>
    </app-page-section>

    <!-- Formules & rémunération -->
    <app-page-section id="deux-formules">
      <div class="grid grid-cols-1 gap-10 lg:grid-cols-[5fr_7fr] lg:items-start">

        <!-- Image portrait avec cross-fade -->
        <div class="relative aspect-[4/5] overflow-hidden rounded-3xl order-2 lg:order-1">
          <img
            src="assets/img/partners-apporteur.webp"
            [alt]="'partners.label.apporteur' | transloco"
            class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            [style.opacity]="openFormula() === 'apporteur' ? 1 : 0"
          />
          <img
            src="assets/img/partners-revendeur.webp"
            [alt]="'partners.label.revendeur' | transloco"
            class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            [style.opacity]="openFormula() === 'revendeur' ? 1 : 0"
          />
          <!-- Gradient fort en bas pour lisibilité du texte -->
          <div class="pointer-events-none absolute inset-0" style="background: linear-gradient(to top, rgba(12,18,28,0.95) 0%, rgba(12,18,28,0.75) 22%, rgba(12,18,28,0.15) 50%, transparent 70%)"></div>
          <!-- Caption -->
          <div class="absolute bottom-0 inset-x-0 p-7">
            @if (openFormula() === 'apporteur') {
              <p class="text-xs font-semibold uppercase tracking-widest text-white/50">{{ 'partners.label.apporteur' | transloco }}</p>
              <p class="mt-1.5 text-sm leading-relaxed text-white/85">{{ 'partners.formulas.caption-apporteur' | transloco }}</p>
            }
            @if (openFormula() === 'revendeur') {
              <p class="text-xs font-semibold uppercase tracking-widest text-white/50">{{ 'partners.label.revendeur' | transloco }}</p>
              <p class="mt-1.5 text-sm leading-relaxed text-white/85" [innerHTML]="'partners.formulas.caption-revendeur' | transloco"></p>
            }
          </div>
        </div>

        <!-- Accordion droite — style Preline minimal -->
        <div class="flex flex-col justify-center order-1 lg:order-2">
          <app-public-tag>{{ 'partners.formulas.tag' | transloco }}</app-public-tag>
          <h2 class="mt-5 text-3xl font-bold md:text-4xl">{{ 'partners.formulas.title' | transloco }}</h2>
          <p class="mt-4 text-base text-gray-500">
            {{ 'partners.formulas.description' | transloco }}
          </p>

          <div class="mt-8 w-full space-y-3 lg:pr-14">

            <!-- Apporteur d'affaire -->
            <div class="rounded-2xl border border-gray-200 px-5 transition-colors duration-200" [class.bg-gray-50]="openFormula() === 'apporteur'">
              <button (click)="selectFormula('apporteur')" class="flex w-full flex-col py-5 text-left">
                <div class="flex w-full items-center justify-between gap-4">
                  <p class="text-lg font-semibold text-gray-800">{{ 'partners.label.apporteur' | transloco }}</p>
                  <i class="far fa-chevron-down shrink-0 text-sm text-gray-800 transition-transform duration-200" [class.rotate-180]="openFormula() === 'apporteur'"></i>
                </div>
                @if (openFormula() === 'apporteur') {
                  <p class="mt-2 text-sm text-gray-500">{{ 'partners.formulas.apporteur-summary' | transloco }}</p>
                }
              </button>
              @if (openFormula() === 'apporteur') {
                <div class="pb-6">
                  <div class="space-y-2.5">
                    @for (step of apporteurSteps; track step.key) {
                      <div class="flex items-start gap-3">
                        <i class="far fa-check mt-1 shrink-0 text-xs text-gray-400"></i>
                        <p class="text-sm text-gray-500">{{ step.key | transloco }}</p>
                      </div>
                    }
                  </div>
                  <div class="mt-7 space-y-1">
                    <p class="text-sm font-semibold text-gray-800 mb-2">{{ 'partners.formulas.gains-title' | transloco }}</p>
                    <p class="text-sm text-gray-500" [innerHTML]="'partners.formulas.apporteur-gain-1' | transloco"></p>
                    <p class="text-sm text-gray-500" [innerHTML]="'partners.formulas.apporteur-gain-2' | transloco"></p>
                  </div>
                </div>
              }
            </div>

            <!-- Revendeur officiel -->
            <div class="rounded-2xl border border-gray-200 px-5 transition-colors duration-200" [class.bg-gray-50]="openFormula() === 'revendeur'">
              <button (click)="selectFormula('revendeur')" class="flex w-full flex-col py-5 text-left">
                <div class="flex w-full items-center justify-between gap-4">
                  <p class="text-lg font-semibold text-gray-800">{{ 'partners.label.revendeur' | transloco }}</p>
                  <i class="far fa-chevron-down shrink-0 text-sm text-gray-800 transition-transform duration-200" [class.rotate-180]="openFormula() === 'revendeur'"></i>
                </div>
                @if (openFormula() === 'revendeur') {
                  <p class="mt-2 text-sm text-gray-500" [innerHTML]="'partners.formulas.revendeur-summary' | transloco"></p>
                }
              </button>
              @if (openFormula() === 'revendeur') {
                <div class="pb-6">
                  <div class="space-y-2.5">
                    @for (step of revendeurSteps; track step.key) {
                      <div class="flex items-start gap-3">
                        <i class="far fa-check mt-1 shrink-0 text-xs text-gray-400"></i>
                        <p class="text-sm text-gray-500" [innerHTML]="step.key | transloco"></p>
                      </div>
                    }
                  </div>
                  <div class="mt-7 space-y-1">
                    <p class="text-sm font-semibold text-gray-800 mb-2">{{ 'partners.formulas.gains-title' | transloco }}</p>
                    <p class="text-sm text-gray-500" [innerHTML]="'partners.formulas.revendeur-gain-1' | transloco"></p>
                    <p class="text-sm text-gray-500" [innerHTML]="'partners.formulas.revendeur-gain-2' | transloco"></p>
                  </div>
                </div>
              }
            </div>

          </div>

          <div class="mt-8 flex flex-wrap gap-3 lg:pr-14">
            <a href="#contact" (click)="scrollTo('contact', $event)">
              <app-button variant="primary" icon="envelope">{{ 'partners.cta-contact' | transloco }}</app-button>
            </a>
            <a [routerLink]="localizedUrl.link('pricing')">
              <app-button variant="outlined" icon="arrow-right">{{ 'partners.formulas.cta-pricing' | transloco }}</app-button>
            </a>
          </div>
        </div>

      </div>
    </app-page-section>

    <!-- Contact -->
    <app-page-section id="contact" backgroundColor="secondary" [waves]="true" [noContainer]="true" padding="lg">
      <div class="container max-w-5xl">
        <div class="mx-auto max-w-2xl text-white">
          <app-public-tag>{{ 'partners.contact.tag' | transloco }}</app-public-tag>
          <h2 class="mt-4 text-white">{{ 'partners.contact.title' | transloco }}</h2>
          <p class="mt-4 text-base text-white/70">
            {{ 'partners.contact.description' | transloco }}
          </p>
          <div class="mt-8 flex flex-wrap gap-3">
            <a href="mailto:contact@ercos.ch">
              <app-button variant="dark" icon="envelope">
                contact@ercos.ch
              </app-button>
            </a>
            <a href="tel:0215253060">
              <app-button variant="dark" icon="phone">
                021 525 30 60
              </app-button>
            </a>
          </div>
        </div>
      </div>
    </app-page-section>

    <div class="h-20 bg-white"></div>
  `,
  standalone: false,
})
export class PartnersResellersPageComponent {
  protected localizedUrl = inject(LocalizedUrlService);

  openFormula = signal<'apporteur' | 'revendeur'>('apporteur');

  selectFormula(formula: 'apporteur' | 'revendeur') {
    const other = formula === 'apporteur' ? 'revendeur' : 'apporteur';
    this.openFormula.set(this.openFormula() === formula ? other : formula);
  }

  scrollTo(id: string, event: Event) {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  apporteurSteps = [
    { key: 'partners.steps.apporteur-1' },
    { key: 'partners.steps.apporteur-2' },
    { key: 'partners.steps.apporteur-3' },
    { key: 'partners.steps.apporteur-4' },
  ];

  revendeurSteps = [
    { key: 'partners.steps.revendeur-1' },
    { key: 'partners.steps.revendeur-2' },
    { key: 'partners.steps.revendeur-3' },
    { key: 'partners.steps.revendeur-4' },
  ];
}
